import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Upload, Eye, ClipboardCheck } from "lucide-react";
import api from "@/lib/api";
import { fmtDate } from "@/components/shared";
import { CompBadge, DocBadge } from "./scBadges";

function ItemRow({ i, sid, onDone, readOnly }) {
  const { t } = useTranslation();
  const ref = useRef(null);
  const upload = async (file) => {
    const fd = new FormData();
    fd.append("file", file); fd.append("doc_type", i.doc_type); fd.append("subcontractor_id", sid);
    if (i.worker_id) fd.append("worker_id", i.worker_id);
    try { await api.post("/subcon/documents", fd); toast.success(t("sc.uploaded")); onDone(); } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const view = async () => {
    const r = await api.get(`/subcon/documents/${i.document_id}/download`, { responseType: "blob" });
    window.open(URL.createObjectURL(r.data), "_blank");
  };
  const tid = `${i.rule_key}-${i.worker_id || "co"}`;
  return (
    <li className="flex flex-wrap items-center gap-3 py-2.5 text-sm" data-testid={`chk-item-${tid}`}>
      <div className="min-w-[180px] flex-1">
        <div className="font-medium text-navy">{i.label}</div>
        <div className="text-xs text-slate-400">{t(`sc.req.${i.requirement}`)}{i.worker_name ? ` · ${i.worker_name}` : ""}{i.source ? ` · ${i.source}` : ""}</div>
      </div>
      <span className="text-xs text-slate-500">{i.valid_until ? `${t("sc.validUntil")} ${fmtDate(i.valid_until)}` : ""}</span>
      <DocBadge v={i.status} testid={`chk-status-${tid}`} />
      {i.document_id && <button onClick={view} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-navy" title={t("sc.view")} data-testid={`chk-view-${tid}`}><Eye className="h-4 w-4" /></button>}
      {!readOnly && i.doc_type !== "SUBUNTERNEHMER_VERTRAG" && (
        <>
          <input ref={ref} type="file" className="hidden" onChange={(e) => e.target.files[0] && upload(e.target.files[0])} />
          <button onClick={() => ref.current?.click()} className="flex items-center gap-1 rounded-full border border-slate-200 px-2.5 py-1 text-xs font-semibold text-navy hover:bg-slate-50" data-testid={`chk-upload-${tid}`}>
            <Upload className="h-3 w-3" /> {i.document_id ? t("sc.replace") : t("sc.upload")}
          </button>
        </>
      )}
    </li>
  );
}

export function Checklist({ sid, readOnly, workerIds, refreshKey }) {
  const { t } = useTranslation();
  const [d, setD] = useState(null);
  const load = () => api.get(`/subcon/companies/${sid}/checklist`).then((r) => setD(r.data)).catch(() => setD(false));
  useEffect(() => { load(); }, [sid, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!d) return null;
  const items = workerIds ? d.items.filter((i) => !i.worker_id || workerIds.includes(i.worker_id)) : d.items;
  return (
    <div className="card-soft p-6" data-testid="sub-checklist">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 font-heading font-bold text-navy"><ClipboardCheck className="h-4 w-4 text-cyan-dark" /> {t("sc.checklist")}</h3>
        <div className="flex items-center gap-2 text-xs text-slate-500">{t("sc.est." + d.establishment)} · {t("sc.overall")} <CompBadge v={d.overall} testid="chk-overall" /></div>
      </div>
      {d.sections.map((s) => {
        const rows = items.filter((i) => i.section === s);
        if (!rows.length) return null;
        return (
          <div key={s} className="mt-3" data-testid={`chk-section-${s}`}>
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{t(`sc.sec.${s}`)}</div>
            <ul className="divide-y divide-slate-100">{rows.map((i) => <ItemRow key={`${i.rule_id}-${i.worker_id}`} i={i} sid={sid} onDone={load} readOnly={readOnly} />)}</ul>
          </div>
        );
      })}
    </div>
  );
}
